import { SyntheticEvent, useState } from 'react'
import { UseFormReturn } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { AddActionDialogType } from '@pages/EntityCreateOrEdit'

import { useFetchDictionariesByCategoryCodeQuery } from '@redux/api'

import { CATEGORIES, TIME_LINE_MODE, TIMELINE_ACTION_COLUMNS } from '@constants'
import {
  ActionRow,
  AutocompleteOption,
  GETShareColumn,
  ModalType,
  TimeLineGridConfig,
} from '@types'

type UseGeneralHandlersType = {
  methods: UseFormReturn<TimeLineGridConfig>
  handleSaveTimeLine: () => void
}

export const useGeneralHandlers = (props: UseGeneralHandlersType) => {
  const { methods, handleSaveTimeLine } = props
  const { t } = useTranslation()

  const { watch, setValue } = methods

  const watchGeneralTimeIntervalRange = watch('timeInterval.dateRange')
  const watchGeneralTimeIntervalShowFilter = watch('timeInterval.showFilter')
  const watchGeneralShowUpdateTimelineButton = watch('showUpdateTimeLineDataButton')
  const watchGeneralActions = watch('actions')
  const watchVariables = watch('variables')
  const watchCode = watch('code')
  const watchTitle = watch('title')
  const watchMode = watch('mode')

  const [generalCurrentTab, setGeneralCurrentTab] = useState(0)
  const [showAddActionDialog, setShowAddActionDialog] = useState(false)
  const [addActionDialogData, setAddActionDialogData] =
    useState<
      Omit<
        AddActionDialogType,
        'createAction' | 'editAction' | 'showDialog' | 'onCloseModal' | 'actionEventsCategoryCode'
      >
    >()

  const { data: bindingValuesAction } = useFetchDictionariesByCategoryCodeQuery(
    CATEGORIES.BINDING_TIME_TABLE_ACTION
  )

  const handleChangeGeneralCurrentTab = (_: SyntheticEvent, value: number) => {
    setGeneralCurrentTab(value)
  }

  const handleOpenAddActionDialog = (modalType: ModalType, id?: string | number) => {
    const actionData = watchGeneralActions?.find(item => item.id === id)

    setShowAddActionDialog(true)
    setAddActionDialogData({
      modalType,
      bindingValuesAction,
      objectCode: '',
      currentRow: actionData as GETShareColumn,
      actionTable: {
        columns: TIMELINE_ACTION_COLUMNS,
        rows: watchGeneralActions,
      },
      columns: [],
    })
  }

  const handleCloseAddActionDialog = (isDirty?: boolean) => {
    if (!isDirty) {
      setShowAddActionDialog(false)
      setAddActionDialogData(undefined)

      return
    }

    if (confirm(t('notifications.leave'))) {
      setShowAddActionDialog(false)
      setAddActionDialogData(undefined)
    }
  }

  const handleDeleteAction = (id: string | number) => {
    setValue(
      'actions',
      watchGeneralActions.filter(item => item.id !== id)
    )

    return handleSaveTimeLine()
  }

  const handleChangeAction = (data: ActionRow) => {
    const columnIndex = watchGeneralActions?.findIndex(item => item.id === data.id)

    if (columnIndex && columnIndex < 0) {
      setValue('actions', watchGeneralActions.concat(data))

      return
    }

    watchGeneralActions[columnIndex] = data
    setValue('actions', watchGeneralActions)
  }

  const handleChangeMode = (value: AutocompleteOption<TIME_LINE_MODE>) => {
    if (value?.id === TIME_LINE_MODE.DATE_TIME) {
      setValue('filterColumnsObjectByObjectX', false, { shouldDirty: true })
    }
  }

  return {
    state: {
      watchGeneralTimeIntervalRange,
      watchGeneralTimeIntervalShowFilter,
      watchGeneralShowUpdateTimelineButton,
      generalCurrentTab,
      watchGeneralActions,
      watchVariables,
      watchCode,
      watchTitle,
      watchMode,
      generalShowAddActionDialog: showAddActionDialog,
      generalAddActionDialogData: addActionDialogData,
    },
    handlers: {
      handleChangeGeneralCurrentTab,
      handleOpenGeneralAddActionDialog: handleOpenAddActionDialog,
      handleCloseGeneralAddActionDialog: handleCloseAddActionDialog,
      handleDeleteGeneralAction: handleDeleteAction,
      handleChangeGeneralAction: handleChangeAction,
      handleChangeGeneralMode: handleChangeMode,
    },
    data: {},
  }
}
